import { debounce } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { RootState } from "../../../../../app/store";
import {
  fetchSkills,
  selectAllSkills,
} from "../../../../../features/skills/skillsSlice";
import { LocalizedSkill } from "../../../../../features/talentProfile/LocalizedGroups";
import { toSortedLocalizedSkills } from "../../../../helpers/formatData";

interface UseTalentSkillsAutocompleteProps {
  workTitleIds?: string[];
  language?: string;
  requireSearchTerm?: boolean;
}

interface UseTalentSkillsAutocompleteReturn {
  skills: LocalizedSkill[];
  isLoading: boolean;
  error?: string;
  setSearchTerm: (term: string) => void;
  resetSearch: () => void;
}

const useTalentSkillsAutocomplete = ({
  workTitleIds,
  language,
  requireSearchTerm = false,
}: UseTalentSkillsAutocompleteProps): UseTalentSkillsAutocompleteReturn => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredSkills, setFilteredSkills] = useState<LocalizedSkill[]>([]);
  const [isFilteredLoading, setIsFilteredLoading] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const localizedSkillIds = useAppSelector(
    (state: RootState) => state.localized?.skill,
  );
  const allSkills = useAppSelector((state: RootState) =>
    selectAllSkills(state),
  );
  const skillsStatus = useAppSelector(
    (state: RootState) => state.skills.status,
  );

  const currentLanguage = language || i18n.language.substring(0, 2);
  const hasWorkTitleFilter = !!(workTitleIds && workTitleIds.length > 0);
  const usesServerSearch = requireSearchTerm || hasWorkTitleFilter;
  const serverSearchTerm = requireSearchTerm ? searchTerm : "";

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, 400),
    [],
  );

  const resetSearch = useMemo(
    () => () => {
      debouncedSetSearchTerm.clear();
      setSearchTerm("");
    },
    [debouncedSetSearchTerm],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.clear();
    };
  }, [debouncedSetSearchTerm]);

  useEffect(() => {
    if (usesServerSearch) return;
    if (skillsStatus !== "idle") return;
    dispatch(fetchSkills({ language: currentLanguage }));
  }, [usesServerSearch, skillsStatus, currentLanguage, dispatch]);

  useEffect(() => {
    if (!usesServerSearch) return;
    if (
      requireSearchTerm &&
      (!serverSearchTerm || serverSearchTerm.trim() === "")
    ) {
      setIsFilteredLoading(false);
      return;
    }

    setIsFilteredLoading(true);

    const filters: { language: string; workTitleIds?: string; term?: string } =
      {
        language: currentLanguage,
      };

    if (hasWorkTitleFilter) {
      filters.workTitleIds = workTitleIds.join(",");
    }

    if (requireSearchTerm && serverSearchTerm.trim() !== "") {
      filters.term = serverSearchTerm.trim();
    }

    dispatch(fetchSkills(filters)).then((action) => {
      if (isFulfilled(action)) {
        setFilteredSkills(
          toSortedLocalizedSkills(action.payload, localizedSkillIds || {}),
        );
      }
      setIsFilteredLoading(false);
    });
  }, [
    usesServerSearch,
    requireSearchTerm,
    serverSearchTerm,
    hasWorkTitleFilter,
    workTitleIds,
    currentLanguage,
    dispatch,
    localizedSkillIds,
  ]);

  const skills = useMemo(
    () =>
      usesServerSearch
        ? filteredSkills
        : toSortedLocalizedSkills(allSkills, localizedSkillIds || {}),
    [usesServerSearch, filteredSkills, allSkills, localizedSkillIds],
  );

  return {
    skills,
    isLoading: usesServerSearch
      ? isFilteredLoading
      : skillsStatus === "pending",
    setSearchTerm: debouncedSetSearchTerm,
    resetSearch,
  };
};

export default useTalentSkillsAutocomplete;
